import { defineStore } from 'pinia'
import { authFetch, getErrorMessage } from '../services/http'

const apiBaseUrl = import.meta.env.VITE_API_BASE_URL

export type InboxNotification = {
  id: string
  type: string
  title: string
  message: string
  entityType?: string | null
  entityId?: string | null
  isRead: boolean
  readAt?: string | null
  createdAt: string
}

export const useNotificationsStore = defineStore('notifications', {
  state: () => {
    return {
      notifications: {
        searchingForAJob: {
          name: 'Searching for a job',
          isEnabled: true,
        },
        hiringSomeone: {
          name: 'Hiring someone',
          isEnabled: false,
        },
        connectingWithOthers: {
          name: 'Connecting with others',
          isEnabled: true,
        },
        postingAndCommenting: {
          name: 'Posting and commenting',
          isEnabled: true,
        },
        messaging: {
          name: 'Messaging',
          isEnabled: true,
        },
        groups: {
          name: 'Groups',
          isEnabled: false,
        },
        pages: {
          name: 'Pages',
          isEnabled: true,
        },
        attendingEvents: {
          name: 'Attending events',
          isEnabled: true,
        },
        newsAndReports: {
          name: 'News and reports',
          isEnabled: false,
        },
        updatingYourProfile: {
          name: 'Updating your profile',
          isEnabled: true,
        },
        verifications: {
          name: 'Verifications',
          isEnabled: true,
        },
      },
      inboxItems: [] as InboxNotification[],
      unreadCount: 0,
      loadingInbox: false,
      inboxError: null as string | null,
    }
  }, 
  actions: {
    async loadInbox(params: { page?: number; pageSize?: number; unreadOnly?: boolean } = {}) {
      this.loadingInbox = true
      this.inboxError = null
      try {
        const query = new URLSearchParams()
        query.set('page', String(params.page ?? 1))
        query.set('pageSize', String(params.pageSize ?? 10))
        if (params.unreadOnly) query.set('unreadOnly', 'true')

        const response = await authFetch(`${apiBaseUrl}/notifications?${query.toString()}`)
        if (!response.ok) {
          const message = await getErrorMessage(response, 'Failed to load notifications')
          throw new Error(message)
        }

        const data = await response.json()
        this.inboxItems = data.items ?? []
        this.unreadCount = data.unreadCount ?? 0
      } catch (error) {
        this.inboxError = error instanceof Error ? error.message : 'Failed to load notifications'
        throw error
      } finally {
        this.loadingInbox = false
      }
    },

    async loadUnreadCount() {
      try {
        const response = await authFetch(`${apiBaseUrl}/notifications/unread-count`)
        if (!response.ok) return
        const data = await response.json()
        this.unreadCount = data.unreadCount ?? 0
      } catch {
        // ignore counter fetch errors
      }
    },

    async markRead(id: string) {
      const response = await authFetch(`${apiBaseUrl}/notifications/${id}/read`, { method: 'POST' })
      if (!response.ok) {
        const message = await getErrorMessage(response, 'Failed to mark notification as read')
        throw new Error(message)
      }

      const item = this.inboxItems.find((entry) => entry.id === id)
      if (item && !item.isRead) {
        item.isRead = true
        item.readAt = new Date().toISOString()
        this.unreadCount = Math.max(this.unreadCount - 1, 0)
      }
    },

    async markAllRead() {
      const response = await authFetch(`${apiBaseUrl}/notifications/read-all`, { method: 'POST' })
      if (!response.ok) {
        const message = await getErrorMessage(response, 'Failed to mark all notifications as read')
        throw new Error(message)
      }

      this.inboxItems = this.inboxItems.map((item) => ({
        ...item,
        isRead: true,
        readAt: item.readAt ?? new Date().toISOString(),
      }))
      this.unreadCount = 0
    },
  }
})
